import { Button } from "@components/ui/button";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { FormSkeleton } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import {
  getTableInfo,
  showToast,
  throwErrorAlert,
} from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { PolicyFormData, PolicySchema } from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import { SiteInformationDate } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

const index = () => {
  const { searchParams, handleAction } = useHandleAction({});
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const modalItem = searchParams.get("modal-item");
  const modalMode = searchParams.get("modal");

  const endpointValue =
    pathname === "privacy-policy"
      ? endpoint["privacypolicy"]
      : pathname === "terms-&-condition"
      ? endpoint["termsconditions"]
      : "";
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<PolicyFormData>({
    mode: "onChange",
    resolver: zodResolver(PolicySchema),
  });
  const result = getTableInfo({
    mode: "default",
    type: pathname || "",
  }) as { title: string; description: string };
  let { title, description } = result;

  const handleMutate = (data: any) => {
    return {
      data,
      endpoint: endpointValue,
      method: METHODS.POST,
    };
  };
  const handleSuccess = async ({
    status,
  }: {
    data: PolicyFormData;
    status: number;
  }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      showToast({
        description: `${pathname.replace(/-/g, " ")} updated`,
        type: "success",
        title: "Success",
      });

      handleAction({
        type: "modal-close",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const { setIsFormPending } = useSelectionStore();

  const handleError = async (error: any) => {
    console.log(error, "error00");
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    setIsFormPending(false);

    const errorMessage = error.data.response.data;
    throwErrorAlert(errorMessage);
  };

  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<SiteInformationDate>
  >({
    endpoint: endpointValue,
    key: `${pathname}`,
    enabled: true,
    // id: modalItem || "",
    window: false,
  });

  /* set default value */
  const { setIsRetrive }: any = usePending();

  useEffect(() => {
    setIsRetrive(modalItem ? fetchPending : false);
    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;
      console.log(firstResult);
      if (firstResult) {
        if (modalMode == "add") {
          return reset({});
        } else {
          reset(() => ({
            description: firstResult?.description || "",
          }));
        }
      }
    }
  }, [fetchData?.data, fetchPending]);

  const { mutate, isPending } = usePostData();
  const onSubmit = (data: PolicyFormData) => {
    //Add Mutation Data
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema = useMemo(
    () => [
      {
        name: "description",
        label: "",
        colSpan: 12,
        type: "editor",
        placeholder: "Enter Description",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-[400px] w-full`,
        visable: true,
      },
    ],
    [setValue, control, errors, isPending]
  );

  if (fetchPending)
    return (
      <>
        <FormSkeleton />
      </>
    );

  return (
    <div className=" ">
      <div className="flex justify-between items-start">
        <ul className="list-none">
          {<li className="text-[32px] font-[500]">{title}</li>}
          {<li className="text-[#7B7679] text-[14px]">{description}</li>}
        </ul>
        <div className="">
          <Button
            disabled={isPending}
            endIcon={isPending ? <Spinner /> : null}
            form={pathname}
            type="submit"
            className="my-5"
          >
            Submit
          </Button>
        </div>
      </div>

      <form
        id={pathname}
        onSubmit={handleSubmit(onSubmit)}
        className="row w-full mt-[20px]"
      >
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </div>
  );
};

export default index;
